import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { InlineTip } from '../components/InlineTip';
import { HelpTooltip } from '../components/HelpTooltip';
import { Save, Building, Phone, MapPin, Tag, Link as LinkIcon, Palette, Rocket, ExternalLink, Type } from 'lucide-react';
import { FONT_PAIRS } from '../constants/fontPairs';
import { DashboardTasks } from '../components/DashboardTasks';
import { DashboardInsights } from '../components/DashboardInsights';
import { BrandProfileSections } from '../components/BrandProfileFields';

const TABS = [
  { id: 'profile', label: 'Brand Profile' },
  { id: 'campaigns', label: 'My Campaigns' },
  { id: 'insights', label: 'Tasks & Insights' },
];

const MyCampaignsTab = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tracking/my-deployments`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    })
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ deployments: [], total_jobs: 0, total_revenue: 0 }));
  }, []);

  if (!data) return <div className="text-light-gray py-12 text-center">Loading your campaigns...</div>;

  if (!data.deployments || data.deployments.length === 0) {
    return (
      <div className="card text-center py-12" data-testid="my-campaigns-empty">
        <Rocket className="mx-auto text-orange mb-4" size={40} />
        <h3 className="text-white font-bold text-xl mb-2">You haven't launched a campaign yet</h3>
        <p className="text-light-gray text-sm mb-6 max-w-md mx-auto">
          Open any campaign and click <span className="text-lime font-bold">"Deploy Tracking"</span> — that marks it as running and starts tracking jobs and revenue here.
        </p>
        <Link to="/vault" className="btn-primary inline-flex items-center gap-2" data-testid="my-campaigns-browse-btn">
          Browse the Campaign Vault →
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="my-campaigns-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <p className="text-light-gray text-sm">Campaigns Running</p>
          <p className="text-4xl font-black text-white" data-testid="my-campaigns-count">{data.deployments.length}</p>
        </div>
        <div className="card">
          <p className="text-light-gray text-sm">Total Jobs Booked</p>
          <p className="text-4xl font-black text-lime" data-testid="my-campaigns-jobs">{data.total_jobs}</p>
        </div>
        <div className="card">
          <p className="text-light-gray text-sm">Est. Total Revenue</p>
          <p className="text-4xl font-black text-orange" data-testid="my-campaigns-revenue">${Number(data.total_revenue).toLocaleString()}</p>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray border-b border-divider">
              <th className="pb-3 pr-4">Campaign</th>
              <th className="pb-3 pr-4">Trade</th>
              <th className="pb-3 pr-4">Launched</th>
              <th className="pb-3 pr-4">Jobs Booked</th>
              <th className="pb-3 pr-4">Job Price</th>
              <th className="pb-3 pr-4">Est. Revenue</th>
              <th className="pb-3">Source</th>
            </tr>
          </thead>
          <tbody>
            {data.deployments.map((d) => (
              <tr key={d.id} className="border-b border-divider/50 hover:bg-black/30" data-testid={`deployment-row-${d.campaign_id}`}>
                <td className="py-3 pr-4">
                  <Link to={`/campaign/${d.campaign_id}`} className="text-white font-semibold hover:text-lime inline-flex items-center gap-1.5">
                    {d.campaign_name} <ExternalLink size={12} className="text-gray" />
                  </Link>
                </td>
                <td className="py-3 pr-4 text-light-gray">{d.trade || '—'}</td>
                <td className="py-3 pr-4 text-light-gray">{d.deploy_date ? new Date(d.deploy_date).toLocaleDateString() : '—'}</td>
                <td className="py-3 pr-4 text-lime font-bold">{d.booking_count || 0}</td>
                <td className="py-3 pr-4 text-white">${Number(d.job_value || 0).toLocaleString()}</td>
                <td className="py-3 pr-4 text-orange font-bold">${Number(d.revenue_estimate || 0).toLocaleString()}</td>
                <td className="py-3 text-gray">{d.booking_provider ? `Auto (${d.booking_provider})` : (d.booking_source === 'manual' ? 'Manual entry' : '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray mt-4">
          Update jobs booked or your price on each campaign's Performance card — or connect your booking system on the <Link to="/integrations" className="text-orange hover:underline">Integrations page</Link> for automatic tracking.
        </p>
      </div>
    </div>
  );
};

const MyBrandPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [profile, setProfile] = useState({
    company_name: '',
    phone: '',
    city: '',
    default_offer: '',
    booking_link: '',
    logo_url: '',
    font_pair: 'bold-modern',
    brand_color_primary: '#E8731A',
    brand_color_secondary: '#0F2740'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/business`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setProfile((p) => ({ ...p, ...data }));
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState('');

  const handleLogoFile = async (file) => {
    setLogoError('');
    const okTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!okTypes.includes(file.type)) { setLogoError('Logo must be a PNG, JPG, SVG, or WebP image.'); return; }
    if (file.size > 2 * 1024 * 1024) { setLogoError('Logo file is too large — keep it under 2MB.'); return; }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setProfile((p) => ({ ...p, logo_url: data.logo_url }));
    } catch (e) {
      setLogoError(e.message || 'Upload failed — try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    setLogoUploading(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/logo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      setProfile((p) => ({ ...p, logo_url: '' }));
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/business`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profile)
      });
      alert('✅ Saved → your info is now in every campaign asset. Next: pick a campaign.');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout variant="light">
        <div className="flex items-center justify-center h-64">
          <div className="text-light-gray">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout variant="light">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <h1 className="text-4xl font-bold text-white mb-6">My Brand</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-divider">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              data-testid={`brand-tab-${t.id}`}
              className={`px-5 py-3 font-bold text-sm rounded-t-lg transition-colors ${
                activeTab === t.id
                  ? 'bg-card-bg text-lime border border-divider border-b-0'
                  : 'text-light-gray hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'campaigns' && <MyCampaignsTab />}

        {activeTab === 'insights' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="brand-insights-tab">
            <DashboardTasks />
            <DashboardInsights />
          </div>
        )}

        {activeTab === 'profile' && (
        <div data-testid="brand-profile-tab">
        {/* Setup Banner */}
        <div className="mb-8 bg-gradient-to-r from-construction-orange/20 to-orange/20 border-2 border-construction-orange/50 rounded-2xl p-6">
          <h2 className="text-2xl font-bold text-white mb-2">🔧 Set Up Your Brand Once — It Applies Everywhere</h2>
          <p className="text-light-gray">
            Fill this in → every ad, email, and landing page gets your name, number, and logo automatically.
          </p>
        </div>

        <div className="card mb-6">
          <h2 className="text-2xl font-bold text-white mb-6">Business Details</h2>
          
          <div className="space-y-6">
            <div>
              <label className="flex items-center gap-2 text-sm text-light-gray mb-2">
                <Building size={16} />
                Company Name
                <HelpTooltip size={13} text="Your real business name — it automatically stamps into every ad, email, and landing page wherever you see [Company]." testId="help-brand-company" />
              </label>
              <input
                type="text"
                value={profile.company_name || ''}
                onChange={(e) => setProfile({...profile, company_name: e.target.value})}
                placeholder="Elite HVAC Services"
                data-testid="brand-company-input"
                className="w-full px-4 py-3 bg-slate-blue border border-divider rounded-lg text-white focus:outline-none focus:border-construction-orange"
              />
              <p className="text-xs text-muted mt-1">Replaces [Company] in all assets</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-light-gray mb-2">
                <Phone size={16} />
                Phone Number
                <HelpTooltip size={13} text="The number homeowners will call. Use one you actually answer during business hours — every campaign points here, and fast answers win jobs." testId="help-brand-phone" />
              </label>
              <input
                type="tel"
                value={profile.phone || ''}
                onChange={(e) => setProfile({...profile, phone: e.target.value})}
                placeholder="(555) 123-4567"
                data-testid="brand-phone-input"
                className="w-full px-4 py-3 bg-slate-blue border border-divider rounded-lg text-white focus:outline-none focus:border-construction-orange"
              />
              <p className="text-xs text-muted mt-1">Replaces [Phone] in all assets</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-light-gray mb-2">
                <MapPin size={16} />
                Primary City
              </label>
              <input
                type="text"
                value={profile.city || ''}
                onChange={(e) => setProfile({...profile, city: e.target.value})}
                placeholder="Phoenix"
                data-testid="brand-city-input"
                className="w-full px-4 py-3 bg-slate-blue border border-divider rounded-lg text-white focus:outline-none focus:border-construction-orange"
              />
              <p className="text-xs text-muted mt-1">Replaces [City] in all assets</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-light-gray mb-2">
                <Tag size={16} />
                Default Offer Text
                <HelpTooltip size={13} text={'A simple deal like "$50 OFF any service." Wherever an asset says [Offer], this text drops in. A clear offer is often the difference between a scroll-past and a call.'} testId="help-brand-offer" />
              </label>
              <input
                type="text"
                value={profile.default_offer || ''}
                onChange={(e) => setProfile({...profile, default_offer: e.target.value})}
                placeholder="$50 OFF any service"
                data-testid="brand-offer-input"
                className="w-full px-4 py-3 bg-slate-blue border border-divider rounded-lg text-white focus:outline-none focus:border-construction-orange"
              />
              <p className="text-xs text-muted mt-1">Replaces [Offer] in all assets</p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-light-gray mb-2">
                <LinkIcon size={16} />
                Booking/Website Link
                <HelpTooltip size={13} text={'Where "Book Now" buttons send people — your booking page or website. Leave it blank and we use your auto-built campaign landing page instead.'} testId="help-brand-link" />
              </label>
              <input
                type="url"
                value={profile.booking_link || ''}
                onChange={(e) => setProfile({...profile, booking_link: e.target.value})}
                placeholder="https://yourwebsite.com/book"
                data-testid="brand-link-input"
                className="w-full px-4 py-3 bg-slate-blue border border-divider rounded-lg text-white focus:outline-none focus:border-construction-orange"
              />
              <p className="text-xs text-muted mt-1">Replaces [Link] and [Website] in all assets</p>
              <div className="mt-3">
                <InlineTip>Where your ad clicks land decides how many turn into calls. A page that matches the ad (same service, same city, phone up top) can cut your cost per lead 20–30% versus sending everyone to your homepage.</InlineTip>
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-light-gray mb-2">
                <Palette size={16} />
                Brand Colors (shown on graphic assets + injected into AI image prompts)
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted mb-1 block">Primary Color</label>
                  <input
                    type="color"
                    value={profile.brand_color_primary || '#E8731A'}
                    onChange={(e) => setProfile({...profile, brand_color_primary: e.target.value})}
                    className="w-full h-12 rounded-lg cursor-pointer"
                  />
                  <p className="text-xs text-muted mt-1">{profile.brand_color_primary}</p>
                </div>
                <div>
                  <label className="text-xs text-muted mb-1 block">Secondary Color</label>
                  <input
                    type="color"
                    value={profile.brand_color_secondary || '#0F2740'}
                    onChange={(e) => setProfile({...profile, brand_color_secondary: e.target.value})}
                    className="w-full h-12 rounded-lg cursor-pointer"
                  />
                  <p className="text-xs text-muted mt-1">{profile.brand_color_secondary}</p>
                </div>
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-light-gray mb-2">
                <Type size={16} />
                Font Style (applied automatically to your landing pages)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="brand-font-picker">
                {Object.values(FONT_PAIRS).map((fp) => {
                  const selected = (profile.font_pair || 'bold-modern') === fp.id;
                  return (
                    <button
                      key={fp.id}
                      type="button"
                      onClick={() => setProfile({ ...profile, font_pair: fp.id })}
                      data-testid={`font-pair-${fp.id}`}
                      className={`text-left p-4 rounded-lg border transition-colors ${selected ? 'border-construction-orange bg-construction-orange/10' : 'border-divider bg-slate-blue hover:border-light-gray/40'}`}
                    >
                      <p className="text-white text-lg mb-0.5" style={{ fontFamily: fp.heading, fontWeight: fp.headingWeight }}>{fp.label}</p>
                      <p className="text-light-gray text-sm mb-1" style={{ fontFamily: fp.body }}>The quick brown fox jumps over the lazy dog.</p>
                      <p className="text-xs text-muted">{fp.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-light-gray mb-2">
                <Building size={16} />
                Company Logo
              </label>
              <div className="flex items-start gap-4">
                {profile.logo_url && (
                  <div className="w-32 h-32 bg-slate-blue rounded-lg border border-divider flex items-center justify-center overflow-hidden shrink-0" data-testid="brand-logo-preview">
                    <img src={profile.logo_url} alt="Company logo" className="max-w-full max-h-full object-contain" />
                  </div>
                )}
                <div className="flex-1">
                  <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files?.[0]) handleLogoFile(e.dataTransfer.files[0]); }}
                    onClick={() => document.getElementById('logo-file-input').click()}
                    data-testid="brand-logo-dropzone"
                    className="border-2 border-dashed border-divider hover:border-lime/50 rounded-lg p-6 text-center cursor-pointer transition-colors"
                  >
                    <p className="text-sm text-light-gray">
                      {logoUploading ? 'Uploading...' : (
                        <>Drag & drop your logo here, or <span className="text-lime font-semibold">click to browse</span></>
                      )}
                    </p>
                    <p className="text-xs text-muted mt-1">PNG, JPG, SVG, or WebP · max 2MB · renders on your public landing pages</p>
                  </div>
                  <input
                    id="logo-file-input"
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    data-testid="brand-logo-file-input"
                    onChange={(e) => { if (e.target.files?.[0]) handleLogoFile(e.target.files[0]); e.target.value = ''; }}
                  />
                  {logoError && <p className="text-xs text-orange mt-2" data-testid="brand-logo-error">{logoError}</p>}
                  {profile.logo_url && (
                    <button
                      type="button"
                      onClick={handleLogoRemove}
                      disabled={logoUploading}
                      data-testid="brand-logo-remove-btn"
                      className="text-xs text-orange hover:text-white mt-2 font-semibold"
                    >
                      Remove logo
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <BrandProfileSections profile={profile} onChange={(key, value) => setProfile((p) => ({ ...p, [key]: value }))} />

          <div className="flex flex-wrap items-center gap-4 mt-8">
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="brand-save-btn"
              className="btn-primary flex items-center gap-2"
            >
              <Save size={20} />
              {saving ? 'Saving...' : 'Save Business Info'}
            </button>
            <Link to="/vault" data-testid="brand-next-step-btn" className="text-lime font-bold text-sm hover:underline">
              Saved? Next: pick your first campaign →
            </Link>
          </div>
        </div>

        {/* Preview */}
        <div className="card bg-slate-blue/30">
          <h3 className="text-xl font-bold text-white mb-4">Preview: How Your Info Appears</h3>
          <div className="bg-deep-navy p-4 rounded-lg border border-divider">
            <p className="text-light-gray text-sm mb-2">Sample marketing text:</p>
            <p className="text-white">
              "Call <span className="text-construction-orange">{profile.company_name || '[Company]'}</span> at{' '}
              <span className="text-construction-orange">{profile.phone || '[Phone]'}</span> for{' '}
              <span className="text-construction-orange">{profile.default_offer || '[Offer]'}</span> on service in{' '}
              <span className="text-construction-orange">{profile.city || '[City]'}</span>. Book now at{' '}
              <span className="text-construction-orange">{profile.booking_link || '[Link]'}</span>"
            </p>
          </div>
        </div>
        </div>
        )}
      </div>
    </Layout>
  );
};

export default MyBrandPage;
